import {
  PromptDefaultOwnerOrganisationId,
  PromptTemplateType,
} from '@repo/shared/dtos/organization'
import { DataProvider } from '@repo/ui/dataProvider'
import { useSearchParams } from 'react-router-dom'

import { PromptTemplates } from '@/components/admin/PromptTemplate'
import { CommonSources } from '@/components/CommonSources.tsx'
import { SkeletonCard } from '@/components/skeletons/SkeletonCard'
import { SearchParamsKeyNames, SearchParamsViewNames } from '@/routes'
import { AccountRoute } from '@/routes/admin/AccountRoute'
import { BeneficiaryAppRoute } from '@/routes/admin/BeneficiaryAppRoute'
import { BeneficiaryListRoute } from '@/routes/admin/BeneficiaryListRoute'
import { BeneficiaryProfileTemplateRoute } from '@/routes/admin/BeneficiaryProfilePlaceholderRoute'
import { CategoriesRoute } from '@/routes/admin/CategoriesRoute'
import { ChatMessageSettingsRoute } from '@/routes/admin/ChatMessageSettingsRoute'
import { DescriptionRoute } from '@/routes/admin/DescriptionRoute'
import { FeedbacksRoute } from '@/routes/admin/FeedbacksRoute'
import { HomeRoute } from '@/routes/admin/HomeRoute'
import { InsightsChatRoute } from '@/routes/admin/InsightsChatRoute'
import { InsightsDashboardRoute } from '@/routes/admin/InsightsDashboardRoute'
import { MembersRoute } from '@/routes/admin/MembersRoute'
import { MethodologyRoute } from '@/routes/admin/MethodologyRoute'
import { ResourcesRoute } from '@/routes/admin/ResourcesRoute'
import { StructuredDataRoute } from '@/routes/admin/StructuredDataRoute'
import { getCurrentOrganization } from '@/store/helpers'
import { Organization } from '@/store/user'

import { InsightsEditRoute } from './admin/InsightsEditRoute'
import { InsightsListRoute } from './admin/InsightsListRoute'
import { InsightsNewRoute } from './admin/InsightsNewRoute'

type ViewConfig = {
  component: React.ComponentType<{ organization: Organization }>
}

const VIEW_CONFIGS: Partial<Record<string, ViewConfig>> = {
  home: { component: HomeRoute },
  [SearchParamsViewNames.ACCOUNT]: { component: AccountRoute },
  [SearchParamsViewNames.DESCRIPTION]: { component: DescriptionRoute },
  [SearchParamsViewNames.METHODOLOGY]: { component: MethodologyRoute },
  [SearchParamsViewNames.CATEGORIES]: { component: CategoriesRoute },
  [SearchParamsViewNames.STRUCTURED_DATA]: { component: StructuredDataRoute },
  [SearchParamsViewNames.SOURCES_PRIVATE]: { component: ResourcesRoute },
  [SearchParamsViewNames.MEMBERS]: { component: MembersRoute },
  [SearchParamsViewNames.SOURCES_COMMON]: {
    component: ({ organization }) => (
      <CommonSources commonSources={Object.keys(organization.commonSources)} />
    ),
  },
  [SearchParamsViewNames.BENEFICIARY_APP]: { component: BeneficiaryAppRoute },
  [SearchParamsViewNames.BENEFICIARY_PROFILE_TEMPLATE]: {
    component: BeneficiaryProfileTemplateRoute,
  },
  [SearchParamsViewNames.BENEFICIARY_LIST]: { component: BeneficiaryListRoute },
  [SearchParamsViewNames.INSIGHTS_DASHBOARD]: { component: InsightsDashboardRoute },
  [SearchParamsViewNames.INSIGHTS_LIST]: { component: InsightsListRoute },
  [SearchParamsViewNames.INSIGHTS_CHAT]: { component: InsightsChatRoute },
  [SearchParamsViewNames.INSIGHTS_NEW]: { component: InsightsNewRoute },
  [SearchParamsViewNames.INSIGHTS_EDIT]: { component: InsightsEditRoute },
  [SearchParamsViewNames.CHAT_MESSAGE_SETTINGS]: { component: ChatMessageSettingsRoute },
  [SearchParamsViewNames.FEEDBACKS]: { component: FeedbacksRoute },
}

export function AdminRoute() {
  const [searchParams] = useSearchParams()
  const currentKey = searchParams.get(SearchParamsKeyNames.View)
  const isPromptTemplate = Object.values(PromptTemplateType).some((v) => v === currentKey)
  const viewConfig = VIEW_CONFIGS[currentKey ?? 'home']

  return (
    <div className="z-10 flex h-full w-full flex-1 overflow-hidden overflow-y-auto">
      <DataProvider
        loadingComponent={<SkeletonCard />}
        selectors={[getCurrentOrganization]}
        render={(organization) => {
          if (isPromptTemplate) {
            return (
              <PromptTemplates
                promptTemplateType={currentKey as PromptTemplateType}
                isDefaultOrganization={organization.id === PromptDefaultOwnerOrganisationId}
                organizationId={organization.id}
              />
            )
          }
          if (viewConfig) {
            const Component = viewConfig.component
            return <Component organization={organization} />
          }
          return null
        }}
      />
    </div>
  )
}
